import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { DefinitionTooltip, DismissibleTag, Tag } from '@carbon/react';

import { formatNumber } from 'utils/number';

import styles from './source-tag-set.module.scss';

/** Never show more than this many tags, however wide the header is. */
const MAX_VISIBLE_TAGS = 5;

/**
 * Budget for the `+N` tag before it has a measured width, in px — roughly a
 * small tag holding three digits.
 */
const FALLBACK_OVERFLOW_TAG_WIDTH = 48;

/**
 * How many hidden sources the `+N` tooltip names before summing up the rest
 * as "and N more"; the asset list's warning and Step 1 summary do the same.
 * A select-all can hide thousands, and a list nobody scrolls through is DOM
 * work for nothing.
 */
export const MAX_OVERFLOW_NAMES = 25;

/**
 * The `+N` label, with separators so a big selection reads at a glance. The
 * rendered and the measured tag both go through here so their widths match.
 *
 * @param {number} count - How many sources the tag stands for
 * @return {string} Tag label
 */
const overflowLabel = (count: number): string =>
  `+${formatNumber({ value: count, minPrecision: 0 })}`;

/**
 * One tag: a table source by its numeric id, or an asset list name by its
 * lowercase key.
 */
export interface SourceTag<TId extends string | number> {
  id: TId;
  name: string;
}

/** Props for {@link SourceTagSet}. */
export interface SourceTagSetProps<TId extends string | number> {
  /** The Step 0 selection, in the order the form holds it. */
  sources: readonly SourceTag<TId>[];
  /** Fires with the id of the source whose close button was clicked. */
  onRemove: (id: TId) => void;
  /**
   * When provided, no tag carries a close button and this label says why.
   * Whether the selection is fixed is the caller's call; two things fix it
   * today. Any step past Select Source: the draft's assets and slots were
   * materialized from the sources, and a removal here would change the
   * heading without rebuilding them, leaving the two disagreeing. And a lone
   * remaining source while editing: the save omits the key on an empty list
   * so a PATCH can never wipe the set, so offering the removal would promise
   * something that silently does not happen.
   */
  lockLabel?: string;
}

/**
 * The selected sources, beside the wizard title, as one removable tag each.
 *
 * Two things this is not: it is not a list of every selection, and it is not
 * decoration. It shows at most five — fewer when the header is too narrow —
 * and collapses the rest into a `+N` tag whose tooltip names them, so a deal
 * combining sixty packages still leaves the title readable. And on the step
 * that owns the selection each tag's close button drops that source, beside
 * the table's own checkbox. Everywhere else `lockLabel` takes the close
 * buttons away, because only Select Source rebuilds the draft behind them.
 *
 * The fit is measured rather than estimated: a hidden copy of the first five
 * tags is rendered off-screen and its real widths are summed against the
 * container, re-measured on resize. Guessing from character counts would
 * either clip a short list or hide tags that would have fit. Only the tags
 * that could ever show are measured, so the work stays flat however many
 * sources a select-all brings in.
 *
 * @param {SourceTagSetProps} props - Selected sources and the remove handler
 * @return {JSX.Element | null} The tag set, or null when nothing is selected
 */
export const SourceTagSet = <TId extends string | number>({
  sources,
  onRemove,
  lockLabel,
}: SourceTagSetProps<TId>): JSX.Element | null => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  // Capped from the first render, not only once measured: a select-all can
  // arrive with thousands, and rendering every one for a frame is the cost the
  // cap exists to avoid.
  const [visibleCount, setVisibleCount] = useState(
    Math.min(sources.length, MAX_VISIBLE_TAGS)
  );

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const available = container.clientWidth;
    const nodes = Array.from(measure.children) as HTMLElement[];
    const candidates = Math.min(sources.length, MAX_VISIBLE_TAGS);
    // The measurement copy renders the candidate tags plus the overflow tag.
    // Bail until it has painted, otherwise the widths are all zero and nothing
    // fits.
    if (nodes.length < candidates + 1) return;

    // `offsetWidth` leaves out the margin the stylesheet spaces tags with, so
    // read it back rather than repeating the value here.
    const gap = parseFloat(getComputedStyle(nodes[0]).marginRight) || 0;
    const widths = nodes
      .slice(0, candidates)
      .map((node) => node.offsetWidth + gap);

    if (
      sources.length <= MAX_VISIBLE_TAGS &&
      widths.reduce((sum, width) => sum + width, 0) <= available
    ) {
      setVisibleCount(sources.length);
      return;
    }

    // Anything hidden needs the `+N` tag, so its width comes out of the budget
    // before the first source tag is placed.
    let used =
      (nodes[candidates]?.offsetWidth ?? FALLBACK_OVERFLOW_TAG_WIDTH) + gap;
    let count = 0;
    for (const width of widths) {
      if (used + width > available) break;
      used += width;
      count += 1;
    }
    setVisibleCount(count);
  }, [sources.length]);

  // The measured tags, by identity: a same-length change (one source swapped
  // for another, a long name for a short one) still changes the widths.
  const measuredKey = sources
    .slice(0, MAX_VISIBLE_TAGS)
    .map((source) => source.id)
    .join(',');
  useLayoutEffect(() => {
    recompute();
  }, [recompute, measuredKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => recompute());
    observer.observe(container);
    return () => observer.disconnect();
  }, [recompute]);

  if (sources.length === 0) return null;

  const visible = sources.slice(0, visibleCount);
  const hiddenCount = sources.length - visible.length;
  const named = sources.slice(visibleCount, visibleCount + MAX_OVERFLOW_NAMES);
  const unnamedCount = hiddenCount - named.length;
  const locked = lockLabel !== undefined;

  return (
    <div className={styles.tags} ref={containerRef}>
      {visible.map((source) => {
        return locked ? (
          // A plain tag, so there is no close button offering a removal that
          // would not take effect. The reason rides on a tooltip rather than
          // the tag's own `title`: Carbon deprecated that and spends it on the
          // label's ellipsis tooltip, and a native `title` never reaches a
          // keyboard or touch user. The tooltip's trigger is focusable and
          // points at the reason with `aria-describedby`.
          <DefinitionTooltip
            key={source.id}
            className={styles.tagTrigger}
            openOnHover
            definition={lockLabel}
          >
            <Tag size="sm">{source.name}</Tag>
          </DefinitionTooltip>
        ) : (
          <DismissibleTag
            key={source.id}
            size="sm"
            text={source.name}
            title={`Remove ${source.name}`}
            dismissTooltipLabel={`Remove ${source.name}`}
            onClose={() => onRemove(source.id)}
          />
        );
      })}
      {hiddenCount > 0 && (
        <DefinitionTooltip
          className={classNames(styles.tagTrigger, styles.overflow)}
          openOnHover
          definition={
            <>
              <ul className={styles.overflowList}>
                {named.map((source) => (
                  <li key={source.id}>{source.name}</li>
                ))}
              </ul>
              {unnamedCount > 0 && (
                <p className={styles.overflowMore}>
                  and {formatNumber({ value: unnamedCount, minPrecision: 0 })}{' '}
                  more
                </p>
              )}
            </>
          }
        >
          <Tag type="high-contrast" size="sm">
            {overflowLabel(hiddenCount)}
          </Tag>
        </DefinitionTooltip>
      )}
      {/* Off-screen measurement copy: the tags that could show, plus the
          widest `+N` it could need. `aria-hidden` because the visible set above is the real one, and
          it stays on `.measure` itself so the measured tags' direct parent
          carries it. The wrapper exists only to clip the copy out of the page's
          scrollable area — see `.measureClip`. */}
      <div className={styles.measureClip}>
        <div className={styles.measure} ref={measureRef} aria-hidden="true">
          {sources.slice(0, MAX_VISIBLE_TAGS).map((source) => (
            <DismissibleTag key={source.id} size="sm" text={source.name} />
          ))}
          <Tag type="high-contrast" size="sm">
            {overflowLabel(sources.length)}
          </Tag>
        </div>
      </div>
    </div>
  );
};
